import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import StockAnalysis from '../components/StockAnalysis';
import { useToast } from '../hooks/use-toast';
import { stockAPI, picksAPI, usageAPI, trendingAPI, searchHistoryAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DashboardHeader from './dashboard/DashboardHeader';
import DailyPicksCard from './dashboard/DailyPicksCard';
import TrendingCard from './dashboard/TrendingCard';
import PopularStocksCard from './dashboard/PopularStocksCard';
import RecentSearchesCard from './dashboard/RecentSearchesCard';
import MarketOverviewCard from './dashboard/MarketOverviewCard';

import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [dailyPicks, setDailyPicks] = useState(null);
  const [popularStocks, setPopularStocks] = useState([]);
  const [trendingStocks, setTrendingStocks] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const { toast } = useToast();
  const { user, loading: authLoading, isPro } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingTrending(true);
        const picks = await picksAPI.getDaily();
        setDailyPicks(picks);
        const trending = await trendingAPI.getStocks(8);
        setTrendingStocks(trending);
        const popular = await trendingAPI.getPopular(5);
        setPopularStocks(popular);
        if (user) {
          const history = await searchHistoryAPI.list(5);
          setSearchHistory(history);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        const popularSymbols = ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'TSLA'];
        const popular = await Promise.all(
          popularSymbols.map((symbol) => stockAPI.getQuote(symbol).catch(() => null)),
        );
        setPopularStocks(popular.filter((s) => s !== null));
      } finally {
        setLoadingTrending(false);
      }
    };
    loadInitialData();
  }, [user]);

  const selectQuoteFor = async (symbol) => {
    try {
      const quote = await stockAPI.getQuote(symbol);
      if (quote) {
        setSelectedStock(quote);
        searchHistoryAPI.save(quote).catch(() => {});
      }
    } catch (error) {
      console.error('Error loading stock:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast({
        title: 'Enter a stock symbol',
        description: 'Please enter a valid stock ticker or company name',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await usageAPI.registerSearch();
      const trimmed = searchQuery.trim();
      // Try direct ticker lookup first. If it 404s (invalid symbol) or the
      // query obviously isn't a ticker (spaces, > 6 chars), fall through
      // to full-text search which resolves company names across NYSE / NASDAQ.
      let quote = null;
      const looksLikeTicker =
        trimmed.length <= 6 && !/\s/.test(trimmed) && /^[a-zA-Z0-9.-]+$/.test(trimmed);
      if (looksLikeTicker) {
        try {
          quote = await stockAPI.getQuote(trimmed.toUpperCase());
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
          // 404 → drop into name-based search below
        }
      }
      if (quote) {
        setSelectedStock(quote);
        setSearchQuery('');
        searchHistoryAPI.save(quote).catch(() => {});
        setSearchHistory((prev) => {
          const filtered = prev.filter((h) => h.symbol !== quote.symbol);
          return [{ ...quote, searched_at: new Date().toISOString() }, ...filtered].slice(0, 5);
        });
      } else {
        const results = await stockAPI.search(trimmed);
        if (results && results.length > 0) {
          setSelectedStock(results[0]);
          setSearchQuery('');
          searchHistoryAPI.save(results[0]).catch(() => {});
          setSearchHistory((prev) => {
            const filtered = prev.filter((h) => h.symbol !== results[0].symbol);
            return [{ ...results[0], searched_at: new Date().toISOString() }, ...filtered].slice(0, 5);
          });
        } else {
          toast({
            title: 'Stock not found',
            description: 'Enter a ticker or company name listed on NYSE / NASDAQ (e.g., BRK-B, Palantir, RDDT).',
          });
        }
      }
    } catch (error) {
      if (error?.response?.data?.code === 'FREE_LIMIT_REACHED') {
        toast({
          title: 'Free limit reached',
          description: error.response.data.message,
          variant: 'destructive',
        });
      } else if (error?.response?.status === 401) {
        toast({
          title: 'Sign in required',
          description: 'Please sign in to analyze stocks.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to search stock. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/pro-access');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle>Sign in to use the AI Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">
              Please use the sign-in button in the top navigation to start a session. This lets us enforce fair
              usage limits and unlock Pro features like journaling.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <DashboardHeader isPro={isPro} onUpgradeClick={handleUpgradeClick} />

        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <Input
                type="text"
                placeholder="Enter stock symbol or company name (e.g., AAPL, Tesla)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-lg"
                data-testid="dashboard-search-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-purple-600 hover:bg-purple-700 px-8 py-6 text-lg"
              disabled={loading}
              data-testid="dashboard-search-btn"
            >
              {loading ? 'Analyzing...' : 'Analyze'}
            </Button>
          </form>
        </div>

        {selectedStock ? (
          <StockAnalysis stock={selectedStock} onBack={() => setSelectedStock(null)} />
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <DailyPicksCard dailyPicks={dailyPicks} onPick={selectQuoteFor} />
              <TrendingCard stocks={trendingStocks} loading={loadingTrending} onPick={selectQuoteFor} />
              <PopularStocksCard
                stocks={popularStocks}
                onPick={(stock) => {
                  setSelectedStock(stock);
                  searchHistoryAPI.save(stock).catch(() => {});
                }}
              />
            </div>
            <div className="space-y-6">
              <RecentSearchesCard history={searchHistory} onPick={selectQuoteFor} />
              <MarketOverviewCard trendingStocks={trendingStocks} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
